import React, { useState, useCallback, useMemo } from 'react';
import moment from 'moment';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { getStatBadges, getBlockTypeLabel, getChartInfo, isStrengthType, isRunType } from './exerciseUtils';
import EditHistoryModal from './EditHistoryModal';
import AreYouSureModal from '../are-you-sure-modal/AreYouSureModal';
import {
  BackButton,
  DetailHeader,
  ExerciseName,
  BlockTypeLabel,
  EntryCard,
  EntryTop,
  EntryDate,
  EntryWorkout,
  EntryActions,
  ActionButton,
  BadgeRow,
  Badge,
  BadgeValue,
  BadgeLabel,
  SectionLabel,
  ChartCard,
  ChartTitle,
  ChartToggleRow,
  ChartToggleOption,
  EmptyState,
  LoadingContainer,
} from './components';

const ProgressionChart = ({ entries, blockType }) => {
  const isStrength = isStrengthType(blockType);
  const isRun = isRunType(blockType);
  const [chartMode, setChartMode] = useState(isRun ? 'distance' : 'weight');

  const chartInfo = useMemo(
    () => getChartInfo(entries, blockType, chartMode),
    [entries, blockType, chartMode],
  );

  if (!chartInfo) return null;

  const chartData = chartInfo.labels.map((label, i) => ({
    date: label,
    value: chartInfo.data[i],
  }));

  const formatYLabel = (val) => {
    if (val >= 1000) return `${(val / 1000).toFixed(1)}k`;
    if (chartMode === 'pace') return Number(val).toFixed(1);
    return Math.round(val);
  };

  return (
    <ChartCard>
      {isStrength && (
        <ChartToggleRow>
          <ChartToggleOption
            active={chartMode === 'weight'}
            onClick={() => setChartMode('weight')}
          >
            Weight
          </ChartToggleOption>
          <ChartToggleOption
            active={chartMode === 'volume'}
            onClick={() => setChartMode('volume')}
          >
            Volume
          </ChartToggleOption>
        </ChartToggleRow>
      )}
      {isRun && (
        <ChartToggleRow>
          <ChartToggleOption
            active={chartMode === 'distance'}
            onClick={() => setChartMode('distance')}
          >
            Distance
          </ChartToggleOption>
          <ChartToggleOption
            active={chartMode === 'pace'}
            onClick={() => setChartMode('pace')}
          >
            Pace
          </ChartToggleOption>
        </ChartToggleRow>
      )}
      <ChartTitle>
        {chartInfo.title}
      </ChartTitle>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
          <CartesianGrid strokeDasharray="" stroke="#e8e8e8" strokeWidth={0.5} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12, fill: '#888', fontFamily: 'Roboto-r' }}
            axisLine={{ stroke: '#e8e8e8' }}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 12, fill: '#888', fontFamily: 'Roboto-r' }}
            tickFormatter={(val) =>
              chartInfo.suffix
                ? `${formatYLabel(val)}${chartInfo.suffix}`
                : formatYLabel(val)
            }
            axisLine={false}
            tickLine={false}
            width={60}
          />
          <Tooltip
            formatter={(val) =>
              chartInfo.suffix ? `${val}${chartInfo.suffix}` : val
            }
            labelStyle={{ fontFamily: 'Roboto-m', fontSize: 13, color: '#373333' }}
            contentStyle={{
              borderRadius: 8,
              border: '1px solid #e2e2e2',
              fontSize: 13,
              fontFamily: 'Roboto-r',
            }}
          />
          <Line
            type="linear"
            dataKey="value"
            stroke="#00FF91"
            strokeWidth={2.5}
            dot={{ r: 5, stroke: '#00FF91', strokeWidth: 2, fill: '#fff' }}
            activeDot={{ r: 7, stroke: '#00FF91', strokeWidth: 2, fill: '#00FF91' }}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

const ExerciseHistoryDetail = ({
  exercise,
  entries,
  loading,
  onBack,
  onEdit,
  onDelete,
}) => {
  const [editingEntry, setEditingEntry] = useState(null);
  const [deletingEntry, setDeletingEntry] = useState(null);

  const handleDelete = useCallback(async () => {
    if (deletingEntry) {
      await onDelete(deletingEntry.uid);
      setDeletingEntry(null);
    }
  }, [deletingEntry, onDelete]);

  return (
    <div>
      <BackButton onClick={onBack}>
        ← Back to list
      </BackButton>
      <DetailHeader>
        <ExerciseName>{exercise.exerciseName}</ExerciseName>
        <BlockTypeLabel>{getBlockTypeLabel(exercise.blockType)}</BlockTypeLabel>
      </DetailHeader>

      {loading ? (
        <LoadingContainer>Loading history...</LoadingContainer>
      ) : !entries.length ? (
        <EmptyState>No history entries found</EmptyState>
      ) : (
        <>
          <ProgressionChart entries={entries} blockType={exercise.blockType} />

          <SectionLabel>History</SectionLabel>
          {entries.map((entry) => {
            const badges = getStatBadges(entry.record, exercise.blockType);
            return (
              <EntryCard key={entry.uid}>
                <EntryTop>
                  <div>
                    <EntryDate>{moment(entry.date).format('DD/MM/YYYY')}</EntryDate>
                    {entry.workoutName && (
                      <EntryWorkout style={{ display: 'block', marginTop: '0.2rem' }}>
                        {entry.workoutName}
                      </EntryWorkout>
                    )}
                  </div>
                  <EntryActions>
                    <ActionButton onClick={() => setEditingEntry(entry)}>
                      Edit
                    </ActionButton>
                    <ActionButton danger onClick={() => setDeletingEntry(entry)}>
                      Delete
                    </ActionButton>
                  </EntryActions>
                </EntryTop>
                {badges.length > 0 && (
                  <BadgeRow>
                    {badges.map((b, i) => (
                      <Badge key={i}>
                        <BadgeValue>{b.value}</BadgeValue>
                        <BadgeLabel>{b.label}</BadgeLabel>
                      </Badge>
                    ))}
                  </BadgeRow>
                )}
              </EntryCard>
            );
          })}
        </>
      )}

      {editingEntry && (
        <EditHistoryModal
          entry={editingEntry}
          blockType={exercise.blockType}
          onSave={onEdit}
          onClose={() => setEditingEntry(null)}
        />
      )}

      <AreYouSureModal
        modalOpen={!!deletingEntry}
        closeModal={() => setDeletingEntry(null)}
        submitModal={handleDelete}
        headingText="Delete Record"
        contentText="Are you sure you want to delete this exercise history entry? This action cannot be undone."
      />
    </div>
  );
};

export default ExerciseHistoryDetail;
